/**
 * Utility functions for computing variant availability with proper cascading.
 *
 * Key principle: Each attribute's availability is determined by OTHER selected filters,
 * NOT by its own filter. This allows users to change their selection within valid options.
 *
 * Example: If case_quantity=3 and count=44 are both selected:
 * - case_quantity availability is based on count=44 filter only (ignoring case_quantity filter)
 * - count availability is based on case_quantity=3 filter only (ignoring count filter)
 */

import type { VariantMember } from "@src/api/ProductDetailApi";

export interface AvailabilityResult {
  available: Array<string | number>;
  unavailable: Array<string | number>;
}

export interface VariantState {
  selectedFilters: Record<string, string | number>;
  allOptions: Record<string, Array<string | number>>;
  members: VariantMember[];
}

/**
 * Computes which options are available for a given attribute.
 *
 * The availability is determined by filtering members using all OTHER selected filters
 * (excluding the current attribute's filter), then extracting unique values for the
 * current attribute from those filtered members.
 *
 * @param attributeKey - The attribute to compute availability for (e.g., 'count')
 * @param allOptions - All possible options for this attribute
 * @param selectedFilters - Currently selected filters (e.g., {case_quantity: 3, count: 44})
 * @param members - All product group members with their variant values
 * @returns Object with available and unavailable option arrays
 */
export function computeAvailableOptions(
  attributeKey: string,
  allOptions: Array<string | number>,
  selectedFilters: Record<string, string | number>,
  members: VariantMember[],
): AvailabilityResult {
  // Build filters excluding the current attribute
  const otherFilters = Object.entries(selectedFilters)
    .filter(([key]) => key !== attributeKey)
    .reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value }),
      {} as Record<string, string | number>,
    );

  // Filter members by OTHER filters only
  const filteredMembers = members.filter((member) =>
    Object.entries(otherFilters).every(
      ([key, value]) => String(member[key]) === String(value),
    ),
  );

  // Extract unique values for this attribute from filtered members
  const availableSet = new Set<string | number>();
  for (const member of filteredMembers) {
    const value = member[attributeKey];
    if (value !== undefined && value !== null && value !== "") {
      availableSet.add(value);
    }
  }

  // Split allOptions into available and unavailable
  const available = allOptions.filter((opt) => availableSet.has(opt));
  const unavailable = allOptions.filter((opt) => !availableSet.has(opt));

  return { available, unavailable };
}

/**
 * Computes availability for all attributes at once.
 *
 * @param allFilterOptions - All options for each attribute
 * @param selectedFilters - Currently selected filters
 * @param members - All product group members
 * @returns Map of attribute key to availability result
 */
export function computeAllAvailability(
  allFilterOptions: Record<string, Array<string | number>>,
  selectedFilters: Record<string, string | number>,
  members: VariantMember[],
): Record<string, AvailabilityResult> {
  const result: Record<string, AvailabilityResult> = {};

  for (const [attributeKey, allOptions] of Object.entries(allFilterOptions)) {
    result[attributeKey] = computeAvailableOptions(
      attributeKey,
      allOptions,
      selectedFilters,
      members,
    );
  }

  return result;
}
